import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useDebouncedValue } from './useDebouncedValue';

type Mermaid = typeof import('mermaid').default;
type MermaidTheme = 'dark' | 'default';

let mermaidPromise: Promise<Mermaid> | null = null;
let renderCounter = 0;
let configuredTheme: MermaidTheme | null = null;

async function getMermaid(): Promise<Mermaid> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => mod.default);
  }
  return mermaidPromise;
}

/**
 * How long the source must stop changing before we lay the diagram out. Agent
 * output arrives token by token and a full mermaid parse + layout per token is
 * pure waste, since every intermediate state is a half-written diagram.
 */
const SETTLE_MS = 200;

/**
 * Ceiling on fence length. Mermaid's own `maxTextSize` default is 50000, well
 * past the point where laying the graph out blocks the main thread for a
 * visible beat, and an agent will happily emit a diagram that large.
 */
const MAX_SOURCE_LENGTH = 20_000;

/**
 * Config keys an `%%{init: ...}%%` directive inside the diagram source may not
 * override.
 *
 * The first six are mermaid's own defaults, restated because passing `secure`
 * at all replaces the default list rather than extending it. `themeCSS` and
 * `themeVariables` are the additions: fence content is model output, which can
 * carry text an agent read from somewhere untrusted, and those two inject
 * arbitrary CSS into the page.
 */
const SECURE_KEYS = [
  'secure', // must be listed so a directive cannot swap the secure-key list itself
  'securityLevel',
  'startOnLoad',
  'maxTextSize',
  'suppressErrorRendering',
  'maxEdges',
  'themeCSS',
  'themeVariables',
  'fontFamily', // %%{init}%% can set fontFamily → CSS custom property; block at the list level
];

/**
 * Mermaid's config is global and `initialize` is *not* queued behind the
 * serialized parse/render pipeline, so a second block calling it can land in
 * the middle of a first block's pending render. Configure once per theme
 * instead of once per block per effect.
 */
function configureMermaid(mermaid: Mermaid, theme: MermaidTheme): void {
  if (configuredTheme === theme) return;
  mermaid.initialize({
    startOnLoad: false,
    theme,
    securityLevel: 'strict',
    fontFamily: 'inherit',
    // Never swap in mermaid's "syntax error" bomb graphic: this hook's callers
    // render their own fallback for source that does not compile.
    suppressErrorRendering: true,
    secure: SECURE_KEYS,
  });
  configuredTheme = theme;
}

export interface MermaidRender {
  /**
   * The most recent source that compiled, or null before the first successful
   * render. Survives a later failure on purpose — see the note in the effect.
   */
  svg: string | null;
  /** Mermaid's parse/render error message, when the latest source did not compile. */
  error: string | null;
}

/**
 * Compile Mermaid source to SVG. Lazy-imports mermaid the first time a diagram
 * appears on the page and re-renders when the active theme flips between dark
 * and light.
 *
 * Agent output streams in token by token, so callers see a run of parse errors
 * for the partial source before the fence closes — treat `error` as "not a
 * diagram *yet*" rather than a terminal state. `svg` and `error` can both be
 * set at once: that is a diagram that rendered earlier in the stream plus the
 * failure of a later, still-partial chunk.
 *
 * The stale-SVG preservation assumes one active fence per streaming turn. If
 * a future answer emits two separate mermaid fences, each rendered by its own
 * `MermaidBlock`, they hold their own state and the assumption holds. If the
 * same block's source is swapped for a wholly different diagram (not a streamed
 * prefix), the old SVG stays visible until the new source parses.
 */
export function useMermaidSvg(source: string): MermaidRender {
  const { resolvedTheme } = useTheme();
  // The app drives the theme through next-themes, but Storybook toggles the
  // `dark` class on <html> directly, with no provider — so fall back to the
  // class when next-themes has not resolved a theme, or diagrams render with
  // light node fills on a dark page.
  const isDark =
    resolvedTheme === 'dark' ||
    (resolvedTheme === undefined &&
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark'));
  const settledSource = useDebouncedValue(source, SETTLE_MS);
  const [render, setRender] = useState<MermaidRender>({ svg: null, error: null });

  useEffect(() => {
    let cancelled = false;
    // Keep the last diagram that compiled, and report the failure alongside it.
    // Streamed source is intermittently valid — `A --> B` parses,
    // `A --> B\n  B -->` does not, `A --> B\n  B --> C` parses again — so
    // dropping the SVG on every failed prefix flips a rendered diagram back to
    // raw text in the middle of an answer.
    const fail = (message: string) => {
      if (!cancelled) setRender((prev) => ({ svg: prev.svg, error: message }));
    };

    void (async () => {
      if (settledSource.length > MAX_SOURCE_LENGTH) {
        fail(`Diagram source is too large to render (limit ${MAX_SOURCE_LENGTH} characters).`);
        return;
      }
      const mermaid = await getMermaid();
      if (cancelled) return;
      configureMermaid(mermaid, isDark ? 'dark' : 'default');
      try {
        // Validate before rendering. `parse` never touches the DOM, while
        // `render` appends a temporary measuring div to <body> *before* it
        // parses — so routing half-written streamed source straight through
        // `render` costs a document reflow per failed prefix. Mermaid does
        // clean that element up when it throws with `suppressErrorRendering`
        // set, so this is about churn, not leaks.
        await mermaid.parse(settledSource);
        // Re-check after parse: the effect may have been cancelled while
        // parse was awaiting (e.g., source changed again mid-stream). Without
        // this guard, a stale effect would still call render() — a wasted
        // DOM mutation and a renderCounter increment per rapid source change.
        if (cancelled) return;
        // Ids must be unique per render: mermaid parks a temporary element
        // under this id while measuring the diagram.
        const { svg } = await mermaid.render(`mermaid-${++renderCounter}`, settledSource);
        if (!cancelled) setRender({ svg, error: null });
      } catch (err) {
        fail(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settledSource, isDark]);

  return render;
}
